import { forwardRef, HTMLAttributes, ReactElement, ReactNode, Ref } from 'react';

import { IComponent } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { basicListStylesDefault, IBasicListStyles } from './BasicList.styles';

interface IBasicListClassNames {
  root: string;
  item: string;
}

export interface IBasicListProps<T extends { [key: string]: any }>
  extends IComponent<IBasicListStyles>,
    HTMLAttributes<HTMLDivElement> {
  idField?: string;
  labelField?: string;
  data?: T[];
  emptyItems?: T[];
  horizontal?: boolean;
  itemRenderer?: (d: T, index: number, last: boolean) => ReactNode | undefined;
  filterFn?: (d: T) => boolean;
  onClickItem?: (d: T) => void;
}

export const BasicList = forwardRef(
  <T extends { [key: string]: any }>(props: IBasicListProps<T>, ref: Ref<HTMLDivElement>) => {
    const {
      idField = 'id' as keyof T,
      labelField = 'name' as keyof T,
      className,
      styles,
      filterFn,
      emptyItems = [],
      data = [],
      horizontal,
      onClickItem,
      itemRenderer,
      ...elementProps
    } = props;
    const classNames = useClassNames<IBasicListStyles, IBasicListClassNames>(
      'BasicList',
      basicListStylesDefault,
      styles,
      className,
      !!horizontal && 'horizontal'
    );
    const len = data.length;
    const _data = len === 0 ? emptyItems : data;
    const renderItems = () => {
      return _data.reduce((items, item, i) => {
        if (!filterFn || filterFn(item)) {
          items.push(
            <div
              className={classNames.item}
              key={`${item[idField]}`}
              onClick={() => {
                if (onClickItem) onClickItem(item);
              }}
            >
              {itemRenderer ? itemRenderer(item, items.length, i === len - 1) : `${item[labelField]}`}
            </div>
          );
        }
        return items;
      }, [] as ReactNode[]);
    };
    return (
      <div {...elementProps} ref={ref} className={classNames.root} data-test={props['data-test'] || 'basic-list'}>
        {renderItems()}
      </div>
    );
  }
) as <T extends { [key: string]: any }>(props: IBasicListProps<T> & { ref?: Ref<HTMLDivElement> }) => ReactElement; // Used type assertion for forwardRef + generic
